import { TRIAL_SENTENCES_LIMIT, TRIAL_QUESTIONS_LIMIT } from './rateLimiter';

export { TRIAL_SENTENCES_LIMIT, TRIAL_QUESTIONS_LIMIT };

const TRIAL_STORAGE_KEY = 'lingua_craft_trial_usage';

interface TrialUsage {
  sentencesRead: number;
  questionsAnswered: number;
  lastResetDate: string;
}

const getDefaultTrialUsage = (): TrialUsage => ({
  sentencesRead: 0,
  questionsAnswered: 0,
  lastResetDate: new Date().toDateString(),
});

const resetTrialIfNeeded = (usage: TrialUsage): TrialUsage => {
  const today = new Date().toDateString();
  if (usage.lastResetDate !== today) {
    return getDefaultTrialUsage();
  }
  return usage;
};

export const getTrialUsage = (): TrialUsage => {
  try {
    const stored = localStorage.getItem(TRIAL_STORAGE_KEY);
    if (!stored) {
      return getDefaultTrialUsage();
    }
    const usage = JSON.parse(stored) as TrialUsage;
    return resetTrialIfNeeded(usage);
  } catch {
    return getDefaultTrialUsage();
  }
};

export const updateTrialUsage = (updates: Partial<TrialUsage>): TrialUsage => {
  const currentUsage = getTrialUsage();
  const newUsage = { ...currentUsage, ...updates };

  try {
    localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(newUsage));
  } catch {
    // localStorage might be unavailable, continue without saving
  }

  return newUsage;
};

export const canReadSentence = (): boolean => {
  const usage = getTrialUsage();
  return usage.sentencesRead < TRIAL_SENTENCES_LIMIT;
};

export const canAnswerQuestion = (): boolean => {
  const usage = getTrialUsage();
  return usage.questionsAnswered < TRIAL_QUESTIONS_LIMIT;
};

export const consumeSentence = (): TrialUsage => {
  const usage = getTrialUsage();
  return updateTrialUsage({
    sentencesRead: usage.sentencesRead + 1,
  });
};

export const consumeQuestion = (): TrialUsage => {
  const usage = getTrialUsage();
  return updateTrialUsage({
    questionsAnswered: usage.questionsAnswered + 1,
  });
};

export const getRemainingTrialSentences = (): number => {
  const usage = getTrialUsage();
  return Math.max(0, TRIAL_SENTENCES_LIMIT - usage.sentencesRead);
};

export const getRemainingTrialQuestions = (): number => {
  const usage = getTrialUsage();
  return Math.max(0, TRIAL_QUESTIONS_LIMIT - usage.questionsAnswered);
};
